
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, RefreshCw, TrendingUp, TrendingDown, DollarSign, Sprout, Database, Target, Table as TableIcon } from "lucide-react";
import { useProfilePrices } from "@/hooks/useProfilePrices";
import { useProfilePriceMatching } from "@/hooks/useProfilePriceMatching";
import { useUserFilters } from "@/hooks/useUserFilters";
import { useState } from "react";
import { ProfileTerminalPricesTable } from "./ProfileTerminalPricesTable";

interface ProfilePrice {
  id: string;
  profile_report_id: string;
  commodity_name: string;
  variety: string | null;
  group_name: string | null;
  price: number;
  unit: string;
  market_type: string;
  report_date: string;
  market_city: string;
  grade: string | null;
  package_type: string | null;
  origin: string | null;
  created_at: string;
  updated_at: string;
}

export const ProfileTerminalPrices = () => {
  const { profilePrices, loading, fetchProfilePrices } = useProfilePrices();
  const { userCrops, userProfile, loading: userCropsLoading } = useUserFilters();
  const { matchAndFetchProfilePrices, loading: matchingLoading, error: matchingError } = useProfilePriceMatching();
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  const handleSmartRefresh = async () => {
    setRefreshing(true);
    try {
      console.log('🎯 Starting smart profile price matching...');
      const result = await matchAndFetchProfilePrices();
      console.log('✅ Smart matching completed:', result);
      
      // Refresh the local data
      await fetchProfilePrices();
    } catch (err) {
      console.error('❌ Smart matching failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleBasicRefresh = async () => {
    setRefreshing(true);
    try {
      // Fetch and store new profile prices from the edge function
      const commodities = [...new Set(userCrops.map(crop => crop.commodity).filter(Boolean))];
      const varieties = [...new Set(userCrops.map(crop => crop.variety).filter(Boolean))];

      console.log('🔄 Calling edge function to fetch and store profile prices...');
      console.log('User location:', userProfile?.location);
      console.log('Commodities:', commodities);
      console.log('Varieties:', varieties);

      const { data: response, error } = await fetch('/api/fetch-usda-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          fetchAndStoreProfilePrices: true,
          userLocation: userProfile?.location,
          commodities: commodities,
          varieties: varieties
        })
      }).then(res => res.json());

      if (error) {
        console.error('❌ Error refreshing profile prices:', error);
      } else if (response?.success) {
        console.log('✅ Successfully refreshed profile prices:', response.message);
        console.log('📊 Stored prices count:', response.data?.length || 0);
        // Refresh the local data
        await fetchProfilePrices();
      } else {
        console.log('⚠️ Edge function response:', response);
      }
    } catch (err) {
      console.error('❌ Exception during profile price refresh:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // If table view is selected, render the table component
  if (viewMode === 'table') {
    return <ProfileTerminalPricesTable />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getPriceColor = (price: number) => {
    if (price > 5) return 'text-green-600';
    if (price > 2) return 'text-blue-600';
    return 'text-yellow-600';
  };

  const getPriceTrend = (price: number) => {
    // Mock trend calculation based on price ranges
    const trend = Math.random() > 0.5 ? 'up' : 'down';
    const percentage = (Math.random() * 10 + 1).toFixed(1);
    return { trend, percentage };
  };

  if (loading || userCropsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-blue-800 flex items-center space-x-2">
            <Building2 size={20} />
            <span>Profile Terminal Market Prices</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-blue-800 flex items-center space-x-2">
            <Building2 size={20} />
            <span>Profile Terminal Market Prices</span>
            <Badge variant="secondary">{profilePrices.length}</Badge>
            {userCrops.length > 0 && (
              <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center space-x-1">
                <Sprout size={12} />
                <span>Profile-Based</span>
              </Badge>
            )}
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 flex items-center space-x-1">
              <Database size={12} />
              <span>Stored</span>
            </Badge>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button 
              onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')} 
              variant="outline" 
              size="sm"
            >
              <TableIcon size={14} className="mr-2" />
              {viewMode === 'cards' ? 'Table View' : 'Card View'}
            </Button>
            <Button 
              onClick={handleSmartRefresh} 
              variant="default" 
              size="sm"
              disabled={refreshing || matchingLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              <Target size={14} className={`mr-2 ${refreshing || matchingLoading ? 'animate-spin' : ''}`} />
              {refreshing || matchingLoading ? 'Smart Matching...' : 'Smart Match'}
            </Button>
            <Button 
              onClick={handleBasicRefresh} 
              variant="outline" 
              size="sm"
              disabled={refreshing || matchingLoading}
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Updating...' : 'Basic Refresh'}
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Terminal market prices using smart matching: Terminal Market → Commodity Groups → Specific Varieties
          {matchingError && (
            <span className="text-red-600 ml-2">
              ⚠️ {matchingError}
            </span>
          )}
        </p>
      </CardHeader>
      <CardContent>
        {profilePrices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No profile prices stored yet</p>
            <div className="space-y-2 text-sm">
              {userCrops.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800 font-medium mb-2">No Crops Configured</p>
                  <p className="text-blue-700">
                    Add crops to your preferences to get personalized terminal market prices.
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 font-medium mb-2">No Stored Prices</p>
                  <p className="text-yellow-700">
                    Use "Smart Match" to intelligently find terminal market prices for your {userCrops.length} configured crops.
                  </p>
                  <div className="mt-2 text-xs text-yellow-600">
                    Location: {userProfile?.location || 'Not set'} | 
                    Crops: {userCrops.map(c => `${c.commodity}${c.variety ? `-${c.variety}` : ''}`).join(', ')}
                  </div>
                  <div className="mt-3 p-3 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                    <p className="font-medium mb-1">🎯 Smart Matching Process:</p>
                    <p>1. Find reports with "Terminal Market" type</p>
                    <p>2. Match commodity groups (e.g., "Fruit", "Nuts") in report names</p>
                    <p>3. Search for your specific commodities and varieties</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-center space-x-2 mt-4">
              <Button 
                onClick={handleSmartRefresh} 
                className="bg-green-600 hover:bg-green-700"
                disabled={refreshing || matchingLoading}
              >
                <Target size={16} className={`mr-2 ${refreshing || matchingLoading ? 'animate-spin' : ''}`} />
                {refreshing || matchingLoading ? 'Smart Matching...' : 'Smart Match Prices'}
              </Button>
              <Button onClick={handleBasicRefresh} variant="outline" disabled={refreshing || matchingLoading}>
                <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Fetching...' : 'Basic Fetch'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2 text-sm text-green-800">
                <Target size={14} />
                <span className="font-medium">Smart-Matched Profile Prices</span>
              </div>
              <p className="text-xs text-green-700 mt-1">
                These prices were intelligently matched using: Terminal Market reports → Commodity groups → Specific varieties.
                Based on your location: {userProfile?.location || 'Unknown'} and {userCrops.length} crop preferences.
                <Button 
                  onClick={() => setViewMode('table')} 
                  variant="link" 
                  size="sm"
                  className="ml-2 text-xs p-0 h-auto"
                >
                  Switch to Table View →
                </Button>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profilePrices.map((price) => {
                const { trend, percentage } = getPriceTrend(price.price);
                
                return (
                  <div 
                    key={price.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {price.commodity_name}
                        </h4>
                        {price.variety && (
                          <p className="text-sm text-blue-600">
                            {price.variety}
                          </p>
                        )}
                        {price.grade && (
                          <p className="text-xs text-gray-500">
                            Grade: {price.grade}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <Badge 
                          className="bg-blue-100 text-blue-800 border-blue-200"
                          variant="outline"
                        >
                          Profile
                        </Badge>
                        {price.origin && (
                          <Badge variant="secondary" className="text-xs">
                            {price.origin}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold flex items-center space-x-1">
                          <DollarSign size={20} className={getPriceColor(price.price)} />
                          <span className={getPriceColor(price.price)}>
                            {price.price.toFixed(2)}
                          </span>
                        </span>
                        <div className="flex items-center space-x-1 text-sm">
                          {trend === 'up' ? (
                            <TrendingUp size={14} className="text-green-600" />
                          ) : (
                            <TrendingDown size={14} className="text-red-600" />
                          )}
                          <span className={trend === 'up' ? 'text-green-600' : 'text-red-600'}>
                            {percentage}%
                          </span>
                        </div>
                      </div>

                      <div className="text-sm text-gray-600">
                        <p className="font-medium">{price.unit}</p>
                        <p>{price.market_city}</p>
                        {price.package_type && (
                          <p className="text-xs">{price.package_type}</p>
                        )}
                      </div>

                      {price.group_name && (
                        <Badge variant="secondary" className="text-xs">
                          {price.group_name}
                        </Badge>
                      )}

                      <div className="text-xs text-gray-500 pt-2 border-t">
                        <p>Report Date: {formatDate(price.report_date)}</p>
                        <p>Stored: {formatDate(price.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Smart Profile-Based Terminal Market Pricing</h4>
              <p className="text-sm text-blue-700 mb-2">
                These prices are fetched using intelligent matching: Terminal Market reports are filtered by commodity groups 
                (Tree Fruits, Tree Nuts, etc.) and then matched to your specific commodities and varieties.
              </p>
              <p className="text-xs text-blue-600">
                * Smart matching ensures higher relevance and accuracy for your specific crop portfolio.
                Data is automatically filtered and stored based on sophisticated keyword matching.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
